/**
 * Sandboxes v2 view (the list page's `?view=v2`): applications to claim from,
 * and the live sandboxes.
 *
 * @module features/sandboxes/components/v2/SandboxV2View
 */
import React from 'react';
import { EmptyStateGraphic, Flex, LoadingOrbit, Page, PageHeader, Text } from '@chatsdet/ui';
import { Terminal } from '@phosphor-icons/react';
import { FeatureOnboardingEmptyState } from '../../../../ui/components/FeatureOnboardingEmptyState';
import { useSandboxV2Applications } from '../../hooks/useSandboxesV2';
import { ApplicationsSection } from './ApplicationsSection';
import { LiveSandboxesSection } from './LiveSandboxesSection';

// Prompt Library entry that owns the setup prompt text; linked by its slug, the stable key.
const SETUP_PROMPT_SLUG = 'set-up-a-cofactor-sandbox-for-an-app';
const ONBOARDING_STEPS = [
  'Your coding agent researches the app and writes its sandbox definition.',
  'It connects the app, from a repository or a built image, as an application.',
  'Applications appear here, ready to claim as a sandbox.',
  'Tests and teammates claim a sandbox to run against a fresh copy of the app.',
];

function EmptyApplications() {
  return (
    <FeatureOnboardingEmptyState
      icon={<Terminal size={48} />}
      graphic={<EmptyStateGraphic />}
      title="Spin up sandboxes for your app"
      whatItIs="A sandbox is a running copy of your app, isolated from everyone else's, that tests and teammates can claim on demand."
      steps={ONBOARDING_STEPS}
      promptLibrarySlug={SETUP_PROMPT_SLUG}
    />
  );
}

export function SandboxV2View({ switcher }: { switcher: React.ReactNode }) {
  const applicationsQuery = useSandboxV2Applications();
  const applications = applicationsQuery.data ?? [];

  return (
    <Page>
      <PageHeader title="Test Environments" actions={switcher} />
      {applicationsQuery.isLoading ? (
        <Flex justify="center" className="py-24">
          <LoadingOrbit size="lg" />
        </Flex>
      ) : applicationsQuery.isError ? (
        <Text color="muted">{applicationsQuery.error.message}</Text>
      ) : applications.length === 0 ? (
        <EmptyApplications />
      ) : (
        <Flex direction="column" gap="lg">
          <ApplicationsSection applications={applications} />
          <LiveSandboxesSection applications={applications} />
        </Flex>
      )}
    </Page>
  );
}
